import * as z from 'zod';

export const ListAvailabilityTimeSlotsRequest = z.object({
  options: z
    .object({
      serviceId: z
        .string()
        .describe(
          'Service ID for which to retrieve time slots. You must specify the ID of an appointment-based service.\nRequired, unless you specify `cursorPaging.cursor`.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      fromLocalDate: z
        .string()
        .describe(
          'Lower boundary for `localStartDate` to include in response.\nEach returned time slot has a `localStartDate` within the provided `fromLocalDate` and `toLocalDate` exclusive.\nRequired, unless you specify `cursorPaging.cursor`.\n\nLocal start date in `YYYY-MM-DDThh:mm:ss` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2026-01-30T13:30:00`.'
        )
        .optional()
        .nullable(),
      toLocalDate: z
        .string()
        .describe(
          'Upper boundary for `localStartDate` to include in response.\nEach returned time slot has a `localStartDate` within the provided `fromLocalDate` and `toLocalDate` exclusive.\nRequired, unless you specify `cursorPaging.cursor`.\n\nLocal end date in `YYYY-MM-DDThh:mm:ss` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2026-01-30T13:30:00`.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database) for adjusting `fromLocalDate` and `toLocalDate`.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/api-reference/business-management/site-properties/properties/get-site-properties).'
        )
        .min(1)
        .max(150)
        .optional()
        .nullable(),
      locations: z
        .array(
          z.object({
            _id: z
              .string()
              .describe(
                '[Location ID](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nAvailable only for business locations.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z.string().describe('Location name.').optional().nullable(),
            formattedAddress: z
              .string()
              .describe('Formatted location address.')
              .optional()
              .nullable(),
            locationType: z.enum(['BUSINESS', 'CUSTOM', 'CUSTOMER']).optional(),
          })
        )
        .min(0)
        .max(5)
        .optional(),
      includeResourceTypeIds: z.array(z.string()).max(100).optional(),
      bookable: z
        .boolean()
        .describe(
          "Whether the time slot is bookable according to the service's booking policies.\nIf not specified, returns both bookable and un-bookable time slots."
        )
        .optional()
        .nullable(),
      bookingPolicyViolations: z
        .object({
          tooEarlyToBook: z
            .boolean()
            .describe(
              'Whether it\'s too early for customers to book the slot.\n\nBy default, all slots are returned. Specifying `{"tooEarlyToBook": false}` returns only those that customers can already book, while specifying `{"tooEarlyToBook": true}` returns only those that can\'t be booked yet.'
            )
            .optional()
            .nullable(),
          earliestBookingDate: z
            .date()
            .describe(
              "Earliest time for booking the slot in `YYYY-MM-DDThh:mm:ss.sssZ` format.\n\n*In responses**: Contains a value when `tooEarlyToBook` is `true`, indicating the earliest time customers can book the slot.\n\n*In requests**: Don't specify a value for this field. Use `tooEarlyToBook` to filter slots that can't be booked yet due to minimum advance booking time restrictions."
            )
            .optional()
            .nullable(),
          tooLateToBook: z
            .boolean()
            .describe(
              'Whether it\'s too late for customers to book the slot.\n\nBy default, all slots are returned. Specifying `{"tooLateToBook": false}` returns only those that customers can still book, while specifying `{"tooLateToBook": true}` returns only those that can no longer be booked.'
            )
            .optional()
            .nullable(),
          bookOnlineDisabled: z
            .boolean()
            .describe(
              'Whether customers can book the service online.\n\nBy default, both services with online booking enabled and disabled are returned. Providing the boolean set to `true` or `false` returns only matching slots.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Indicators for service's booking policy violations.\nAllows filtering for time slots with specific violation types based on [booking policies](https://dev.wix.com/docs/api-reference/business-solutions/bookings/policies/booking-policies/introduction)). When you don't specify `bookingPolicyViolations` filters, all slots are returned regardless of their booking policy status."
        )
        .optional(),
      timeSlotsPerDay: z
        .number()
        .int()
        .describe(
          "Maximum number of slots to return for each day in the specified time range.\nIf `bookable` filter isn't specified, bookable slots are returned first."
        )
        .optional()
        .nullable(),
      cursorPaging: z
        .object({
          limit: z
            .number()
            .int()
            .describe(
              'Number of results to load.\n\nDefault: `1000`.\nMax: `1000`.'
            )
            .min(0)
            .max(1000)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor-based paging configuration.\nEnables fetching results in smaller chunks by setting a limit on the number of results.\nFor consistent pagination behavior, use the same `limit` value throughout a pagination sequence.\nWhen specifying a new `limit` in follow-up requests, the API respects the new value.'
        )
        .optional(),
      customerChoices: z
        .object({
          durationInMinutes: z
            .number()
            .int()
            .describe(
              "Duration selected by the customer in minutes. Based on the customer's selection from [service options and variants](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/service-options-and-variants/introduction).\nWhen using [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction), we recommend passing only `addOnIds` instead of `durationInMinutes`. Wix Bookings then calculates the total duration automatically. If you specify both `addOnIds` and `durationInMinutes`, `durationInMinutes` must equal the service duration plus all selected add-on durations."
            )
            .min(1)
            .max(44639)
            .optional()
            .nullable(),
          addOnIds: z.array(z.string()).max(21).optional(),
        })
        .describe(
          "Selected customer choices for service configuration.\nIf not specified, uses the service's default configuration.\n\nWhen using [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction), we recommend passing only `addOnIds` and omitting `durationInMinutes`. Wix Bookings then calculates the total duration automatically. If you specify both `addOnIds` and `durationInMinutes`, `durationInMinutes` must equal the service duration plus all selected add-on durations."
        )
        .optional(),
      resourceTypes: z
        .array(
          z.object({
            resourceTypeId: z
              .string()
              .describe(
                '[Resource type ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            resourceIds: z.array(z.string()).max(135).optional(),
          })
        )
        .max(3)
        .optional(),
    })
    .optional(),
});
export const ListAvailabilityTimeSlotsResponse = z.object({
  timeSlots: z
    .array(
      z.object({
        serviceId: z
          .string()
          .describe(
            '[Service ID] (https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction).\n\nAvailable only for single-service bookings. For multi-service bookings, this field is empty and individual service IDs are provided in `nestedTimeSlots`.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        localStartDate: z
          .string()
          .describe(
            'Local start date of the time slot in `YYYY-MM-DDThh:mm:ss` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2026-01-30T13:30:00`.\n\nFor multi-service bookings, this represents the start time of the first service in the sequence.'
          )
          .optional()
          .nullable(),
        localEndDate: z
          .string()
          .describe(
            'Local end date of the time slot in `YYYY-MM-DDThh:mm:ss` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2026-01-30T14:00:00`.\n\nFor multi-service bookings, this represents the end time of the last service in the sequence.'
          )
          .optional()
          .nullable(),
        bookable: z
          .boolean()
          .describe(
            "Whether customers can book the slot according to the service's [booking policies](https://dev.wix.com/docs/api-reference/business-solutions/bookings/policies/booking-policies/introduction).\n\nFor multi-service bookings, this is `true` only when all services in the sequence comply with their respective booking policies."
          )
          .optional()
          .nullable(),
        location: z
          .object({
            _id: z
              .string()
              .describe(
                '[Location ID](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nAvailable only for business locations.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z.string().describe('Location name.').optional().nullable(),
            formattedAddress: z
              .string()
              .describe('Formatted location address.')
              .optional()
              .nullable(),
            locationType: z
              .enum(['BUSINESS', 'CUSTOM', 'CUSTOMER'])
              .describe('Location type.')
              .optional(),
          })
          .describe(
            'Information about where the business provides the service to the customer.'
          )
          .optional(),
        eventInfo: z
          .object({
            eventId: z
              .string()
              .describe('Event ID.')
              .min(36)
              .max(250)
              .optional()
              .nullable(),
            waitingList: z
              .object({
                totalCapacity: z
                  .number()
                  .int()
                  .describe('Total number of spots in the waitlist.')
                  .min(1)
                  .optional()
                  .nullable(),
                remainingCapacity: z
                  .number()
                  .int()
                  .describe(
                    'Number of remaining spots in the waitlist.\nFor example, an event with a waitlist for 10 people and 3 registrants, results in a remaining capacity of `7`.'
                  )
                  .min(0)
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about the event's waitlist. Available only if the service has a waitlist."
              )
              .optional(),
            eventTitle: z
              .string()
              .describe('Event title.')
              .min(1)
              .max(200)
              .optional()
              .nullable(),
          })
          .describe(
            'Information about the [event](https://dev.wix.com/docs/api-reference/business-management/calendar/events-v3/introduction) related to the slot.\nAvailable only for classes. Not available for appointment-based services and courses.'
          )
          .optional(),
        totalCapacity: z
          .number()
          .int()
          .describe(
            'Total number of spots for the slot.\n\nFor multi-service bookings, this is always `1` because customers book the entire service sequence as a single unit.'
          )
          .min(1)
          .max(1000)
          .optional()
          .nullable(),
        remainingCapacity: z
          .number()
          .int()
          .describe(
            "Remaining number of spots for the slot.\n- For appointment bookings: Either `1` (available) or `0` (unavailable).\n- For classes: Total capacity minus booked spots. Doesn't account for waitlist reservations. For classes with waitlists, use `bookableCapacity` to get the actual number of spots customers can book.\n- For courses: Total capacity minus booked spots. Courses don't currently support waitlists."
          )
          .min(0)
          .max(1000)
          .optional()
          .nullable(),
        bookableCapacity: z
          .number()
          .int()
          .describe(
            "Number of spots that customers can book for the slot.\nCalculated as the remaining capacity minus the spots reserved for the waitlist.\nIf the service has no waitlist, identical to `remainingCapacity`.\n\nFor multi-service bookings, this is either `1` (sequence can be booked) or `0` (sequence can't be booked)."
          )
          .optional()
          .nullable(),
        bookingPolicyViolations: z
          .object({
            tooEarlyToBook: z
              .boolean()
              .describe(
                'Whether it\'s too early for customers to book the slot.\n\nBy default, all slots are returned. Specifying `{"tooEarlyToBook": false}` returns only those that customers can already book, while specifying `{"tooEarlyToBook": true}` returns only those that can\'t be booked yet.'
              )
              .optional()
              .nullable(),
            earliestBookingDate: z
              .date()
              .describe(
                "Earliest time for booking the slot in `YYYY-MM-DDThh:mm:ss.sssZ` format.\n\n*In responses**: Contains a value when `tooEarlyToBook` is `true`, indicating the earliest time customers can book the slot.\n\n*In requests**: Don't specify a value for this field. Use `tooEarlyToBook` to filter slots that can't be booked yet due to minimum advance booking time restrictions."
              )
              .optional()
              .nullable(),
            tooLateToBook: z
              .boolean()
              .describe(
                'Whether it\'s too late for customers to book the slot.\n\nBy default, all slots are returned. Specifying `{"tooLateToBook": false}` returns only those that customers can still book, while specifying `{"tooLateToBook": true}` returns only those that can no longer be booked.'
              )
              .optional()
              .nullable(),
            bookOnlineDisabled: z
              .boolean()
              .describe(
                'Whether customers can book the service online.\n\nBy default, both services with online booking enabled and disabled are returned. Providing the boolean set to `true` or `false` returns only matching slots.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Information about booking policy violations for the slot.\n\nFor multi-service bookings, this aggregates violations from all services in the sequence.'
          )
          .optional(),
        availableResources: z
          .array(
            z.object({
              resourceTypeId: z
                .string()
                .describe(
                  '[Resource type ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              resources: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Resource ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .string()
                      .describe('Resource name.')
                      .min(1)
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
              hasMoreAvailableResources: z
                .boolean()
                .describe(
                  'Whether there are more available resources for the slot than those listed in `resources`.'
                )
                .optional()
                .nullable(),
            })
          )
          .min(0)
          .optional(),
        nestedTimeSlots: z
          .array(
            z.object({
              serviceId: z
                .string()
                .describe('Service ID of the nested time slot.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              localStartDate: z
                .string()
                .describe(
                  'Local start date of the nested time slot in `YYYY-MM-DDThh:mm:ss` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2026-01-30T13:30:00`.'
                )
                .optional(),
              localEndDate: z
                .string()
                .describe(
                  'Local end date of the nested time slot in `YYYY-MM-DDThh:mm:ss` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2026-01-30T13:30:00`.'
                )
                .optional(),
              availableResources: z
                .array(
                  z.object({
                    resourceTypeId: z
                      .string()
                      .describe(
                        '[Resource type ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    resources: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('Resource ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          name: z
                            .string()
                            .describe('Resource name.')
                            .min(1)
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                    hasMoreAvailableResources: z
                      .boolean()
                      .describe(
                        'Whether there are more available resources for the slot than those listed in `resources`.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(10)
                .optional(),
              scheduleId: z
                .string()
                .describe(
                  "The schedule ID associated with this nested time slot.\nSame as the service's schedule ID."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
          )
          .max(8)
          .optional(),
        nonBookableReasons: z
          .object({
            noRemainingCapacity: z
              .boolean()
              .describe(
                'Whether the slot is fully booked with no remaining capacity.'
              )
              .optional()
              .nullable(),
            violatesBookingPolicy: z
              .boolean()
              .describe(
                "Whether booking the slot violates any of the service's booking policies."
              )
              .optional()
              .nullable(),
            reservedForWaitingList: z
              .boolean()
              .describe(
                "Whether the slot is reserved for the waitlist. A new customer can't book the reserved slot."
              )
              .optional()
              .nullable(),
            eventCancelled: z
              .boolean()
              .describe('Whether the related event is cancelled.')
              .optional()
              .nullable(),
          })
          .describe("Information about why customers can't book the time slot.")
          .optional(),
        scheduleId: z
          .string()
          .describe(
            "Schedule ID associated with this time slot.\nSame as the service's schedule ID."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .max(1000)
    .optional(),
  timeZone: z
    .string()
    .describe(
      'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database) for adjusting `fromLocalDate` and `toLocalDate`.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/api-reference/business-management/site-properties/properties/get-site-properties).'
    )
    .min(1)
    .max(150)
    .optional()
    .nullable(),
  cursorPagingMetadata: z
    .object({
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe('Offset that was requested.')
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Paging metadata for the next page of results.\nContains a cursor if there are more than 1000 results.'
    )
    .optional(),
});
export const GetAvailabilityTimeSlotRequest = z.object({
  serviceId: z
    .string()
    .describe(
      'Service ID of the time slot.\nYou must specify the ID of an appointment-based service.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  localStartDate: z
    .string()
    .describe(
      'Local start date of the time slot in `YYYY-MM-DDThh:mm:ss` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2026-01-30T13:30:00`.'
    ),
  localEndDate: z
    .string()
    .describe(
      'Local end date of the time slot in `YYYY-MM-DDThh:mm:ss` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2026-01-30T13:30:00`.'
    ),
  timeZone: z
    .string()
    .describe(
      'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database) for adjusting `fromLocalDate` and `toLocalDate`.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/api-reference/business-management/site-properties/properties/get-site-properties).'
    )
    .min(1)
    .max(150),
  location: z
    .object({
      _id: z
        .string()
        .describe(
          '[Location ID](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nAvailable only for business locations.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z.string().describe('Location name.').optional().nullable(),
      formattedAddress: z
        .string()
        .describe('Formatted location address.')
        .optional()
        .nullable(),
      locationType: z.enum(['BUSINESS', 'CUSTOM', 'CUSTOMER']).optional(),
    })
    .describe(
      'Location to filter time slots by.\n\nFor business locations, you must specify a location ID.\nWhen specifying a location ID, all other location field filters are ignored.'
    ),
  options: z
    .object({
      includeResourceTypeIds: z.array(z.string()).max(100).optional(),
      resourceTypes: z
        .array(
          z.object({
            resourceTypeId: z
              .string()
              .describe(
                '[Resource type ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            resourceIds: z.array(z.string()).max(135).optional(),
          })
        )
        .max(3)
        .optional(),
    })
    .optional(),
});
export const GetAvailabilityTimeSlotResponse = z.object({
  timeSlot: z
    .object({
      serviceId: z
        .string()
        .describe(
          '[Service ID] (https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction).\n\nAvailable only for single-service bookings. For multi-service bookings, this field is empty and individual service IDs are provided in `nestedTimeSlots`.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      localStartDate: z
        .string()
        .describe(
          'Local start date of the time slot in `YYYY-MM-DDThh:mm:ss` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2026-01-30T13:30:00`.\n\nFor multi-service bookings, this represents the start time of the first service in the sequence.'
        )
        .optional()
        .nullable(),
      localEndDate: z
        .string()
        .describe(
          'Local end date of the time slot in `YYYY-MM-DDThh:mm:ss` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2026-01-30T14:00:00`.\n\nFor multi-service bookings, this represents the end time of the last service in the sequence.'
        )
        .optional()
        .nullable(),
      bookable: z
        .boolean()
        .describe(
          "Whether customers can book the slot according to the service's [booking policies](https://dev.wix.com/docs/api-reference/business-solutions/bookings/policies/booking-policies/introduction).\n\nFor multi-service bookings, this is `true` only when all services in the sequence comply with their respective booking policies."
        )
        .optional()
        .nullable(),
      location: z
        .object({
          _id: z
            .string()
            .describe(
              '[Location ID](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nAvailable only for business locations.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          name: z.string().describe('Location name.').optional().nullable(),
          formattedAddress: z
            .string()
            .describe('Formatted location address.')
            .optional()
            .nullable(),
          locationType: z
            .enum(['BUSINESS', 'CUSTOM', 'CUSTOMER'])
            .describe('Location type.')
            .optional(),
        })
        .describe(
          'Information about where the business provides the service to the customer.'
        )
        .optional(),
      eventInfo: z
        .object({
          eventId: z
            .string()
            .describe('Event ID.')
            .min(36)
            .max(250)
            .optional()
            .nullable(),
          waitingList: z
            .object({
              totalCapacity: z
                .number()
                .int()
                .describe('Total number of spots in the waitlist.')
                .min(1)
                .optional()
                .nullable(),
              remainingCapacity: z
                .number()
                .int()
                .describe(
                  'Number of remaining spots in the waitlist.\nFor example, an event with a waitlist for 10 people and 3 registrants, results in a remaining capacity of `7`.'
                )
                .min(0)
                .optional()
                .nullable(),
            })
            .describe(
              "Information about the event's waitlist. Available only if the service has a waitlist."
            )
            .optional(),
          eventTitle: z
            .string()
            .describe('Event title.')
            .min(1)
            .max(200)
            .optional()
            .nullable(),
        })
        .describe(
          'Information about the [event](https://dev.wix.com/docs/api-reference/business-management/calendar/events-v3/introduction) related to the slot.\nAvailable only for classes. Not available for appointment-based services and courses.'
        )
        .optional(),
      totalCapacity: z
        .number()
        .int()
        .describe(
          'Total number of spots for the slot.\n\nFor multi-service bookings, this is always `1` because customers book the entire service sequence as a single unit.'
        )
        .min(1)
        .max(1000)
        .optional()
        .nullable(),
      remainingCapacity: z
        .number()
        .int()
        .describe(
          "Remaining number of spots for the slot.\n- For appointment bookings: Either `1` (available) or `0` (unavailable).\n- For classes: Total capacity minus booked spots. Doesn't account for waitlist reservations. For classes with waitlists, use `bookableCapacity` to get the actual number of spots customers can book.\n- For courses: Total capacity minus booked spots. Courses don't currently support waitlists."
        )
        .min(0)
        .max(1000)
        .optional()
        .nullable(),
      bookableCapacity: z
        .number()
        .int()
        .describe(
          "Number of spots that customers can book for the slot.\nCalculated as the remaining capacity minus the spots reserved for the waitlist.\nIf the service has no waitlist, identical to `remainingCapacity`.\n\nFor multi-service bookings, this is either `1` (sequence can be booked) or `0` (sequence can't be booked)."
        )
        .optional()
        .nullable(),
      bookingPolicyViolations: z
        .object({
          tooEarlyToBook: z
            .boolean()
            .describe(
              'Whether it\'s too early for customers to book the slot.\n\nBy default, all slots are returned. Specifying `{"tooEarlyToBook": false}` returns only those that customers can already book, while specifying `{"tooEarlyToBook": true}` returns only those that can\'t be booked yet.'
            )
            .optional()
            .nullable(),
          earliestBookingDate: z
            .date()
            .describe(
              "Earliest time for booking the slot in `YYYY-MM-DDThh:mm:ss.sssZ` format.\n\n*In responses**: Contains a value when `tooEarlyToBook` is `true`, indicating the earliest time customers can book the slot.\n\n*In requests**: Don't specify a value for this field. Use `tooEarlyToBook` to filter slots that can't be booked yet due to minimum advance booking time restrictions."
            )
            .optional()
            .nullable(),
          tooLateToBook: z
            .boolean()
            .describe(
              'Whether it\'s too late for customers to book the slot.\n\nBy default, all slots are returned. Specifying `{"tooLateToBook": false}` returns only those that customers can still book, while specifying `{"tooLateToBook": true}` returns only those that can no longer be booked.'
            )
            .optional()
            .nullable(),
          bookOnlineDisabled: z
            .boolean()
            .describe(
              'Whether customers can book the service online.\n\nBy default, both services with online booking enabled and disabled are returned. Providing the boolean set to `true` or `false` returns only matching slots.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Information about booking policy violations for the slot.\n\nFor multi-service bookings, this aggregates violations from all services in the sequence.'
        )
        .optional(),
      availableResources: z
        .array(
          z.object({
            resourceTypeId: z
              .string()
              .describe(
                '[Resource type ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            resources: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Resource ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  name: z
                    .string()
                    .describe('Resource name.')
                    .min(1)
                    .optional()
                    .nullable(),
                })
              )
              .optional(),
            hasMoreAvailableResources: z
              .boolean()
              .describe(
                'Whether there are more available resources for the slot than those listed in `resources`.'
              )
              .optional()
              .nullable(),
          })
        )
        .min(0)
        .optional(),
      nestedTimeSlots: z
        .array(
          z.object({
            serviceId: z
              .string()
              .describe('Service ID of the nested time slot.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            localStartDate: z
              .string()
              .describe(
                'Local start date of the nested time slot in `YYYY-MM-DDThh:mm:ss` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2026-01-30T13:30:00`.'
              )
              .optional(),
            localEndDate: z
              .string()
              .describe(
                'Local end date of the nested time slot in `YYYY-MM-DDThh:mm:ss` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2026-01-30T13:30:00`.'
              )
              .optional(),
            availableResources: z
              .array(
                z.object({
                  resourceTypeId: z
                    .string()
                    .describe(
                      '[Resource type ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  resources: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Resource ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Resource name.')
                          .min(1)
                          .optional()
                          .nullable(),
                      })
                    )
                    .optional(),
                  hasMoreAvailableResources: z
                    .boolean()
                    .describe(
                      'Whether there are more available resources for the slot than those listed in `resources`.'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(10)
              .optional(),
            scheduleId: z
              .string()
              .describe(
                "The schedule ID associated with this nested time slot.\nSame as the service's schedule ID."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
        )
        .max(8)
        .optional(),
      nonBookableReasons: z
        .object({
          noRemainingCapacity: z
            .boolean()
            .describe(
              'Whether the slot is fully booked with no remaining capacity.'
            )
            .optional()
            .nullable(),
          violatesBookingPolicy: z
            .boolean()
            .describe(
              "Whether booking the slot violates any of the service's booking policies."
            )
            .optional()
            .nullable(),
          reservedForWaitingList: z
            .boolean()
            .describe(
              "Whether the slot is reserved for the waitlist. A new customer can't book the reserved slot."
            )
            .optional()
            .nullable(),
          eventCancelled: z
            .boolean()
            .describe('Whether the related event is cancelled.')
            .optional()
            .nullable(),
        })
        .describe("Information about why customers can't book the time slot.")
        .optional(),
      scheduleId: z
        .string()
        .describe(
          "Schedule ID associated with this time slot.\nSame as the service's schedule ID."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe('Retrieved time slot.')
    .optional(),
  timeZone: z
    .string()
    .describe(
      'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database) for adjusting `fromLocalDate` and `toLocalDate`.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/api-reference/business-management/site-properties/properties/get-site-properties).'
    )
    .min(1)
    .max(150)
    .optional()
    .nullable(),
});
